import { Grid } from '@material-ui/core';
import React, { FC, useMemo, useState } from 'react';
import { Row } from 'react-table';
import { makeStyles } from '@ui';
import { usePaymentProviderContext } from '../../Session/PaymentProviderContext';
import { ContainerModern, Loader, PureTable, Typography } from '../common';
import { FilterBar } from './FilterBar';
import { InvoiceExport } from './InvoiceExport';
import { useInvoiceTableColumns } from './invoiceTableConfig';
import { OutstandingBalance } from './OutstandingBalance';
import { UnappliedCredits } from './UnappliedCredits';
import { PayNowBar } from './PayNowBar';
import { BillingTableItem } from './types';
import { useInvoiceRefetch } from './useInvoiceRefetch';
import { useInvoiceTableItems } from './useBillingQueries';
import { useWebshop } from '../webshop/WebshopContext';

const defaultSortState = {
  sortBy: [{ id: 'Status' }, { id: 'DueDate' }],
};

const useStyles = makeStyles((theme) => ({
  grid: { marginBottom: theme.spacing(5) },
  rightAligned: { marginLeft: 'auto' },
}));

export const Invoices: FC = () => {
  const { currencyOptions, paymentProvider } = usePaymentProviderContext();
  useWebshop();
  const classes = useStyles();
  const { items, isLoading, refetch, isError } = useInvoiceTableItems();
  const [selectedInvoiceRows, setSelectedInvoiceRows] = useState<Row<BillingTableItem>[]>([]);
  const columns = useInvoiceTableColumns(currencyOptions);
  const filters = useMemo(() => <FilterBar />, []);
  const selectedItems = useMemo(
    () => selectedInvoiceRows.map(({ original }) => original),
    [selectedInvoiceRows],
  );
  useInvoiceRefetch(refetch);

  return (
    <ContainerModern>
      {isLoading && <Loader />}

      {isError && <Typography>Invoices could not be loaded!</Typography>}

      {!isLoading && !isError && (
        <>
          <Grid
            container
            justify='space-between'
            alignItems='flex-end'
            classes={{ root: classes.grid }}
            wrap='nowrap'
          >
            <Grid item container wrap='nowrap' spacing={3}>
              <Grid item>
                <OutstandingBalance invoices={items} options={currencyOptions} />
              </Grid>
              <Grid item>
                <UnappliedCredits />
              </Grid>
            </Grid>
            <Grid item classes={{ root: classes.rightAligned }}>
              {paymentProvider && <InvoiceExport invoices={items} account={paymentProvider} />}
            </Grid>
          </Grid>

          <PureTable<BillingTableItem>
            data={items}
            columns={columns}
            sortBy={defaultSortState}
            onSelect={setSelectedInvoiceRows}
            Filters={filters}
          />

          <PayNowBar items={selectedItems} currencyOptions={currencyOptions} />
        </>
      )}
    </ContainerModern>
  );
};
